import { event } from "../util/EventUtil.js";
import { KeyCode } from "../util/KeyCode.js";

export class TouchEmulator {
    private static _created: TouchEmulator = null;

    private _cursors: Cursor[] = [];
    private _current: Cursor = null;
    private _disposables: Disposable[] = [];

    private constructor() {
        if (TouchEmulator._created) {
            console.warn("You cannot create mulitiple instances of TouchEmulator.");
            return;
        }
        TouchEmulator._created = this;

        this._disposables.push(
            event.on({
                type: "mousedown",
                scope: document,
                capture: true,
                passive: false,
                listener: (e) => {
                    e.event.stopImmediatePropagation();
                    e.event.preventDefault();
                    this._current = new Cursor(e.event);
                    this._cursors.push(this._current);
                    if (e.event.ctrlKey) {
                        this._current = null;
                    }
                }
            }),
            event.on({
                type: "mousemove",
                scope: document,
                capture: true,
                passive: false,
                listener: (e) => {
                    e.event.stopImmediatePropagation();
                    if (!this._current) {
                        return;
                    }
                    this._current.move(e.event);
                }
            }),
            event.on({
                type: "mouseup",
                scope: document,
                capture: true,
                passive: false,
                listener: (e) => {
                    e.event.stopImmediatePropagation();
                    if (!this._current) {
                        return;
                    }
                    if (!e.event.ctrlKey) {
                        this._current.up(e.event);
                        this._cursors.splice(this._cursors.indexOf(this._current), 1);
                    }
                    this._current = null;
                }
            }),
            event.on({
                type: "keyup",
                scope: document,
                listener: (e) => {
                    //If shift key is released.
                    if (KeyCode.is(e.event, KeyCode.SHIFT)){
                        this.clear();
                    }
                }
            }),
            event.on({
                type: "blur",
                scope: window,
                listener: (e) => {
                    this.clear();
                }
            })
        );
    }

    private clear(): void {
        for (var cursor of this._cursors) {
            cursor.up();
        }
    }

    public dispose(): void {
        TouchEmulator._created = null;

        this.clear();
        this._cursors = null;

        for(let d of this._disposables){
            d.dispose();
        }
        this._disposables = null;

    }

    public static create(): void {
        console.warn("Use e5.TouchEmulator.enabled = true");
        this.enabled = true;
    }

    public static get enabled(): boolean {
        return Boolean(this._created);
    }

    public static set enabled(value: boolean) {
        if (this.enabled == value) {
            return;
        }

        if (value) {
            if (!this.isSupported) {
                console.warn("TouchEmulator is not supported.");
                return;
            }
            this._created = new TouchEmulator();
        } else {

            this._created.dispose();
            this._created = null;
        }
    }

    public static get isSupported(): boolean {
        return Boolean((<any>window)["TouchEvent"]);
    }
}

class Cursor {
    private static _uid: number = 1024;

    private _uid: number;
    private _element: HTMLDivElement;
    private _event: MouseEvent;
    private _touch: Touch;
    private _target: HTMLElement;

    constructor(e: MouseEvent) {
        this._uid = Cursor._uid++;

        var div = document.createElement("div");
        div.style.position = "absolute";
        div.style.top = "0";
        div.style.left = "0";
        div.style.pointerEvents = "none";
        div.style.width = "10px";
        div.style.height = "10px";
        div.style.marginLeft = "-5px";
        div.style.marginTop = "-5px";
        div.style.zIndex = "100000";
        div.style.backgroundColor = "rgba(255, 0, 255, 0.5)";
        div.style.borderRadius = "50%";
        document.body.appendChild(div);

        this._element = div;

        this.down(e);
    }

    private down(e: MouseEvent): void {
        // console.log("down");
        this._event = e;
        this._target = e.target as HTMLElement;
        this.updateTouch(e);
        this._element.style.transform = "translate(" + e.pageX + "px, " + e.pageY + "px)";
        this._target.dispatchEvent(new TouchEvent("touchstart", {
            altKey: e.altKey,
            shiftKey: e.shiftKey,
            bubbles: true,
            changedTouches: [this._touch],
        }));
    }

    public move(e: MouseEvent): void {
        // console.log("move");
        this._event = e;
        this.updateTouch(e);
        this._element.style.transform = "translate(" + e.pageX + "px, " + e.pageY + "px)";
        this._target.dispatchEvent(new TouchEvent("touchmove", {
            altKey: e.altKey,
            shiftKey: e.shiftKey,
            bubbles: true,
            changedTouches: [this._touch],
        }));
    }

    public up(e: MouseEvent = null): void {
        // console.log("up");
        e = e || this._event
        this.updateTouch(e);
        this._target.dispatchEvent(new TouchEvent("touchend", {
            altKey: e.altKey,
            shiftKey: e.shiftKey,
            bubbles: true,
            changedTouches: [this._touch],
        }));
        this._element.remove();
        this._touch = null;
    }

    private updateTouch(e: MouseEvent): void {
        this._touch = new Touch({
            pageX: e.pageX,
            pageY: e.pageY,
            clientX: e.clientX,
            clientY: e.clientY,
            target: e.target,
            identifier: this._uid
        });
    }
}